/**
 * `InventoryCoverStats` — the days-of-cover panel for one variant.
 *
 * Exported for embedding beside a SKU anywhere in the app (the catalog SKU
 * screen is its first host). It fails soft: a load error renders a compact
 * retry rather than taking its host screen down with it.
 *
 * `days_of_cover` and `average_daily_demand` are unbounded-scale decimal
 * strings computed server-side over days the item actually had stock — the
 * widget states that so nobody re-derives the number from on-hand ÷ demand
 * and gets a different answer.
 */
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@erp/ui";

import { ApiError } from "@/lib/api/errors";
import { formatDateTime, formatNumber } from "@/lib/format";

import type { VariantStats } from "../api";
import { useVariantStats } from "../queries";
import { AmountText, QuantityText, RatioText } from "./decimal-text";

function scopeLabel(stats: VariantStats): string {
  if (stats.group_by === "warehouse") return stats.warehouse_code;
  if (stats.group_by === "branch") return stats.branch_code;
  return "";
}

export interface InventoryCoverStatsProps {
  variantId: string;
  /** Scope the statistics to one warehouse (omit for the whole company). */
  warehouseId?: string;
  /** Analysis window in days; the server defaults it when omitted. */
  days?: number;
  /** False when the session lacks `inventory.report.read`. */
  canRead?: boolean;
  className?: string;
}

export function InventoryCoverStats({
  variantId,
  warehouseId,
  days,
  canRead = true,
  className,
}: InventoryCoverStatsProps) {
  const { t, i18n } = useTranslation();
  const query = useVariantStats(
    variantId,
    {
      ...(warehouseId === undefined ? {} : { warehouse_id: warehouseId }),
      ...(days === undefined ? {} : { days }),
    },
    canRead,
  );

  const isNoHistory = query.error instanceof ApiError && query.error.status === 404;

  if (!canRead) return null;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          <h2 className="text-base">{t("inventory.cover.title", "Stock cover")}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : isNoHistory ? (
          // 404 here means "this variant has no position in this scope", which
          // is an empty state, not a failure.
          <p className="text-sm text-muted-foreground">
            {t("inventory.cover.empty", "No stock history yet in this scope.")}
          </p>
        ) : query.isError || query.data === undefined ? (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t("inventory.cover.error", "Cover statistics are unavailable.")}
            </span>
            <Button type="button" size="sm" variant="outline" onClick={() => void query.refetch()}>
              {t("actions.retry")}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span dir="ltr" className="font-mono font-medium">
                {query.data.sku}
              </span>
              {scopeLabel(query.data) === "" ? null : (
                <Badge variant="outline">{scopeLabel(query.data)}</Badge>
              )}
              <Badge variant="secondary">
                {t("inventory.cover.window", "{{days}} day window", {
                  days: query.data.analysis_window_days,
                })}
              </Badge>
            </div>

            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div>
                <dt className="text-xs text-muted-foreground">
                  {t("inventory.stock.columns.onHand", "On hand")}
                </dt>
                <dd>
                  <QuantityText value={query.data.on_hand_qty} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">
                  {t("inventory.stock.columns.available", "Available")}
                </dt>
                <dd>
                  <QuantityText value={query.data.available_qty} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">
                  {t("inventory.common.value", "Value")}
                </dt>
                <dd>
                  <AmountText value={query.data.stock_value} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">
                  {t("inventory.slowMovers.avgDemand", "Avg daily demand")}
                </dt>
                <dd>
                  <RatioText value={query.data.average_daily_demand} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">
                  {t("inventory.slowMovers.activeDays", "Days in stock")}
                </dt>
                <dd>{formatNumber(query.data.active_stock_days, i18n.language)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">
                  {t("inventory.slowMovers.cover", "Days of cover")}
                </dt>
                <dd>
                  {query.data.days_of_cover === undefined ? (
                    <Badge
                      variant="warning"
                      aria-label={t("inventory.slowMovers.noDemand", "No demand")}
                    >
                      ∞
                    </Badge>
                  ) : (
                    <RatioText value={query.data.days_of_cover} fractionDigits={1} />
                  )}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>
                {t("inventory.reports.asOf", "As of {{when}}", {
                  when: formatDateTime(query.data.as_of, i18n.language),
                })}
              </span>
              {/* Nested provider: the shell supplies one, isolated renders need their own. */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help underline decoration-dotted">
                      {t("inventory.slowMovers.zeroStockNote", "Zero-stock days are excluded")}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t(
                      "inventory.slowMovers.zeroStockTooltip",
                      "Average demand and days of cover divide by days the item actually had stock, so a stock-out does not flatter the numbers.",
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
